import { ENV_VARS } from '@/env';
import { isEmpty } from '@/utils/object';
import { Col, Form, Row } from 'antd';
import { clone, pickBy } from 'es-toolkit';
import { t } from 'i18next';
import moment from 'moment';
import { useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { QUOTE_SEARCH_FILTER_DATES_FIELDS } from '../../../contants/Fields';
import { getSearchQuoteDefaultFilters } from '../../../contants/ShipmentEnums';
import { DEFAULT_DATE_FORMAT } from '../../../contants/common';
import { setWarningModal } from '../../../redux/common/CommonActions';
import {
  getSearchQuoteListInProgress,
  setSearchQuoteFilters,
  setSearchQuoteList,
} from '../../../redux/shipment/ShipmentActions';
import { valueToString } from '../../../utilz';
import Button from '../../components/Buttons/Button';
import SearchQuoteFilterContent from './components/SearchQuoteFilterContent';

const SearchQuoteFilters = () => {
  const { state } = useLocation();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { quoteKey } = useParams();
  const isHBUser = ENV_VARS.REACT_APP_THEME === 'halliburton';

  const {
    status,
    transportationGroup,
    quoteNumber,
    transportationProvider,
    hazardousClass,
    spotQuoteCreatedFrom,
    spotQuoteCreateTo,
    prevState,
  } = state || {};

  const searchQuoteFilters = useSelector((state) => state.shipment.searchQuoteFilters, shallowEqual);

  const updateFiltersAndFetchQuotes = (data) => {
    dispatch(setSearchQuoteList({ data: [], progressing: true, isFirstTime: false }));
    dispatch(setSearchQuoteFilters(data));
    dispatch(getSearchQuoteListInProgress());
  };

  useEffect(() => {
    if (!prevState) {
      dispatch(setSearchQuoteFilters(getSearchQuoteDefaultFilters()));
      dispatch(
        setSearchQuoteList({
          data: [],
          isFirstTime: true,
        }),
      );
    }
  }, []);

  useEffect(() => {
    const defaultFilters = getSearchQuoteDefaultFilters();
    const commonFilters = {
      ...defaultFilters,
      ...searchQuoteFilters,
      quotestatus: status,
      transporterKey: transportationProvider,
      transportationGroup: transportationGroup,
      quoteNumber: quoteKey || quoteNumber || '',
      spotQuoteCreatedFrom: spotQuoteCreatedFrom || '',
      spotQuoteCreateTo: spotQuoteCreateTo || '',
      hazardousClass: hazardousClass || '',
    };

    const data = quoteKey
      ? {
          ...defaultFilters,
          ...searchQuoteFilters,
          quoteNumber: quoteKey || quoteNumber || '',
          spotQuoteCreatedFrom: spotQuoteCreatedFrom || '',
          spotQuoteCreateTo: spotQuoteCreateTo || '',
        }
      : commonFilters;

    if (status) {
      updateFiltersAndFetchQuotes(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    status,
    transportationGroup,
    transportationProvider,
    quoteKey,
    spotQuoteCreateTo,
    hazardousClass,
    spotQuoteCreatedFrom,
  ]);

  useEffect(() => {
    if (searchQuoteFilters) {
      const data = clone(searchQuoteFilters);
      for (const key in data) {
        if (QUOTE_SEARCH_FILTER_DATES_FIELDS.includes(key)) {
          if (data[key]) {
            data[key] = moment(data[key]);
          } else {
            data[key] = '';
          }
        }
      }
      data.quotestatus = data.quotestatus ? data.quotestatus.split(',') : [];

      data.createdBy = data?.createdBy ? data?.createdBy?.split(',') : [];
      data.createdBy = data.createdBy.map((val) => Number(val));
      form.setFieldsValue(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuoteFilters]);

  const handleFilterSubmit = (formData) => {
    // to prevent the form state mutations
    let data = clone(formData);
    if (data.createdByType) {
      // biome-ignore lint/performance/noDelete: <explanation>
      delete data.createdByType;
    }
    const isFilterIsEmpty = isEmpty(
      pickBy(data, (value) =>
        value ? (typeof value === 'object' ? Boolean(value?.length) : Boolean(String(value).length)) : false,
      ),
    );
    if (
      isFilterIsEmpty &&
      !data.spotQuoteCreatedFrom &&
      !data.spotQuoteCreateTo &&
      !data.expectedShipFromDate &&
      !data.expectedShipTodate
    ) {
      dispatch(
        setWarningModal({
          open: true,
          description: 'Please enter at least one search criteria',
        }),
      );
    } else {
      data = clone({ ...getSearchQuoteDefaultFilters(), ...data });
      for (const key in data) {
        if (QUOTE_SEARCH_FILTER_DATES_FIELDS.includes(key) && data[key]) {
          data[key] = data[key].format(DEFAULT_DATE_FORMAT);
        }
      }

      data.quotestatus = Array.isArray(data.quotestatus) ? data.quotestatus.join(',') : [];
      data.createdBy = typeof data.createdBy === 'object' ? valueToString(data.createdBy.join(',')) : '';
      dispatch(
        setSearchQuoteList({
          data: [],
          progressing: true,
          isFirstTime: false,
        }),
      );
      dispatch(
        setSearchQuoteFilters({
          ...data,
        }),
      );
      dispatch(getSearchQuoteListInProgress());
    }
  };

  const handleClear = () => {
    form.resetFields();
    form.setFieldsValue({
      CreatedDateFrom: moment(new Date()).subtract(1, 'months'),
      CreatedDateTo: moment(new Date()),
      quotestatus: [],
    });
  };

  return (
    <div className="d-flex flex-direction-column overflow-hidden h-100 br-4">
      <div className="custom-scroll-bar overflow-x-hidden mb-15 flex-1">
        <Form onFinish={handleFilterSubmit} form={form} initialValues={{ createdByType: 1 }}>
          <SearchQuoteFilterContent form={form} />
        </Form>
      </div>
      <Row className="px-15" gutter={15}>
        <Col sm={24} md={12}>
          <Button
            fullWidth
            type="default"
            onClick={handleClear}
            buttonType="button"
            id="shipment-filter-clear"
            className={isHBUser ? 'hb-button' : ''}
          >
            {t('translation:default.buttons.clear')}
          </Button>
        </Col>
        <Col sm={24} md={12}>
          <Button
            fullWidth
            type="primary"
            onClick={() => form.submit()}
            buttonType="submit"
            id="shipment-filter-search"
            className={isHBUser ? 'hb-button' : ''}
          >
            {t('translation:default.buttons.search')}
          </Button>
        </Col>
      </Row>
    </div>
  );
};
export default SearchQuoteFilters;
